// utils/uploadDB.js
// IndexedDB utility for storing pending photo uploads and mutations
// Includes unique file key generation to prevent collisions

import { nanoid } from "nanoid";

import { logError } from "../utils/appUtils";

const DB_NAME = "PhotoUploadsDB";
const DB_VERSION = 4; // Bumped to ensure new stores are created
const STORE_PENDING = "pendingUploads";
const STORE_MUTATIONS = "pendingMutations";
const STORE_TOMBSTONES = "tombstones";
const STORE_PENDING_DELETES = "pendingDeletes";

let dbInstance = null;

const buildIdbError = (errorInput, metadata = {}, fallbackMessage) => {
  const hasMetadata =
    metadata &&
    typeof metadata === "object" &&
    Object.keys(metadata).length > 0;
  const mergeContext = (errorInstance) => {
    if (hasMetadata) {
      errorInstance.context = {
        ...(errorInstance.context || {}),
        ...metadata,
      };
    }
    return errorInstance;
  };

  if (errorInput instanceof Error) {
    return mergeContext(errorInput);
  }

  if (
    errorInput &&
    typeof errorInput === "object" &&
    errorInput.message &&
    typeof errorInput.message === "string"
  ) {
    const normalized = new Error(errorInput.message);
    normalized.originalError = errorInput;
    return mergeContext(normalized);
  }

  const normalized = new Error(
    typeof errorInput === "string" && errorInput.length > 0
      ? errorInput
      : fallbackMessage || "IndexedDB operation failed",
  );

  if (
    errorInput &&
    typeof errorInput === "object" &&
    Object.keys(errorInput).length > 0
  ) {
    normalized.originalError = errorInput;
  }

  return mergeContext(normalized);
};

/**
 * INTERNAL HELPER: Intelligent Error Filtering
 * Decides whether an error is worth sending to the backend DB/Logger.
 */
const reportFailure = (operation, error, metadata = {}) => {
  const errName =
    error?.name || (typeof error === "string" ? "ErrorString" : "UnknownError");
  const errMsg = error?.message || (typeof error === "string" ? error : "");

  // 1. IGNORE: Transient or User-driven errors (Noise reduction)
  if (errName === "AbortError") return;
  if (errName === "InvalidStateError" && errMsg.includes("closing")) return;

  // 2. WARNING: Operational issues
  if (errName === "QuotaExceededError") {
    logError(
      `[Warning] IDB Quota Exceeded during ${operation}`,
      buildIdbError(
        error,
        {
          severity: "warning",
          details: "User device out of storage space",
          ...metadata,
        },
        "IndexedDB quota exceeded",
      ),
    );
    return;
  }

  if (errName === "Blocked") {
    logError(
      `[Warning] IDB Blocked during ${operation}`,
      buildIdbError(
        error,
        {
          severity: "warning",
          details: "Database upgrade blocked by open connection",
          ...metadata,
        },
        "IndexedDB blocked",
      ),
    );
    return;
  }

  // 3. CRITICAL: Actual code bugs or system failures
  logError(
    `[Critical] IDB Failure: ${operation}`,
    buildIdbError(
      error,
      {
        errorName: errName,
        errorMessage: errMsg,
        ...metadata,
      },
      `IndexedDB failure during ${operation}`,
    ),
  );
};

const closeDbInstance = () => {
  if (!dbInstance) return;
  try {
    dbInstance.close();
  } catch (err) {
    reportFailure("closeDB", err);
  }
  dbInstance = null;
};

const attachVersionChangeHandler = (db) => {
  if (!db) return;
  db.onversionchange = () => {
    reportFailure(
      "versionchange",
      {
        name: "VersionChange",
        message: "Closing IndexedDB connection for upgrade",
      },
      { dbName: DB_NAME },
    );
    closeDbInstance();
  };
};

const markLogged = (error) => {
  if (error && typeof error === "object") {
    // eslint-disable-next-line no-param-reassign
    error.__idbLogged = true;
  }
};

const requestPromise = (request, operationName, metadata) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      const err = request.error || new Error("IndexedDB request failed");
      reportFailure(operationName, err, metadata);
      markLogged(err);
      reject(err);
    };
  });

const withStore = async (
  storeName,
  mode,
  operationName,
  executor,
  { fallback, metadata, rethrow } = {},
) => {
  try {
    const db = await openDB();
    const store = db.transaction([storeName], mode).objectStore(storeName);
    return await executor(store);
  } catch (error) {
    if (!error?.__idbLogged && !rethrow) {
      reportFailure(`${operationName}_catch`, error, metadata);
    }
    if (rethrow) throw error;
    return fallback;
  }
};

const pruneByTimestamp = (
  storeName,
  indexName,
  maxAgeMs,
  operationName,
  metadata,
) => {
  const now = Date.now();
  let removed = 0;
  return withStore(
    storeName,
    "readwrite",
    operationName,
    (store) =>
      new Promise((resolve) => {
        const cursorRequest = store.index(indexName).openCursor();
        cursorRequest.onsuccess = ({ target: { result: cursor } }) => {
          if (!cursor) {
            resolve(removed);
            return;
          }
          if (now - cursor.value.timestamp > maxAgeMs) {
            cursor.delete();
            removed += 1;
          }
          cursor.continue();
        };
        cursorRequest.onerror = () => {
          reportFailure(operationName, cursorRequest.error, metadata);
          resolve(removed);
        };
      }),
    { fallback: 0, metadata },
  );
};

const deleteByIndexMatch = (
  storeName,
  indexName,
  value,
  operationName,
  metadata,
) => {
  let removed = 0;
  return withStore(
    storeName,
    "readwrite",
    operationName,
    (store) =>
      new Promise((resolve) => {
        const cursorRequest = store
          .index(indexName)
          .openCursor(IDBKeyRange.only(value));
        cursorRequest.onsuccess = ({ target: { result: cursor } }) => {
          if (!cursor) {
            resolve(removed);
            return;
          }
          cursor.delete();
          removed += 1;
          cursor.continue();
        };
        cursorRequest.onerror = () => {
          reportFailure(operationName, cursorRequest.error, metadata);
          resolve(removed);
        };
      }),
    { fallback: 0, metadata },
  );
};

/**
 * SAFE WRAPPER: Generate file key with collision prevention
 * Uses original business getFileKey format but adds UUID for offline scenarios
 */
export const generateSafeFileKey = (file, parentId, getFileKey) => {
  const originalKey = getFileKey(file, parentId);

  // Online: Use original business key format
  if (navigator.onLine) {
    return originalKey;
  }

  // Offline: Add nanoid protection
  const id = nanoid(10);
  const parts = originalKey.split("/");
  const fileName = parts.pop();
  const path = parts.join("/");

  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) {
    return `${path}/${fileName}_${id}`;
  }

  const name = fileName.substring(0, dotIndex);
  const ext = fileName.substring(dotIndex);

  return `${path}/${name}_${id}${ext}`;
};

// ==========================================
// INDEXEDDB OPERATIONS
// ==========================================

/**
 * Open or create IndexedDB database
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reportFailure("openDB", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      attachVersionChangeHandler(dbInstance);
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Helper to log transaction errors during upgrade
      if (event.target.transaction) {
        event.target.transaction.onerror = (e) =>
          reportFailure("onupgradeneeded_transaction", e.target.error);
      }

      // 1. Pending Uploads Store
      if (!db.objectStoreNames.contains(STORE_PENDING)) {
        const pendingStore = db.createObjectStore(STORE_PENDING, {
          keyPath: "key",
        });
        pendingStore.createIndex("timestamp", "timestamp", { unique: false });
        pendingStore.createIndex("parentId", "parentId", { unique: false });
      }

      // 2. Pending Mutations Store
      if (!db.objectStoreNames.contains(STORE_MUTATIONS)) {
        const mutationStore = db.createObjectStore(STORE_MUTATIONS, {
          keyPath: "key",
        });
        mutationStore.createIndex("timestamp", "timestamp", { unique: false });
        mutationStore.createIndex("parentId", "parentId", { unique: false });
        mutationStore.createIndex("attempts", "attempts", { unique: false });
      }

      // 3. Tombstones Store
      if (!db.objectStoreNames.contains(STORE_TOMBSTONES)) {
        const tombstoneStore = db.createObjectStore(STORE_TOMBSTONES, {
          keyPath: "key",
        });
        tombstoneStore.createIndex("timestamp", "timestamp", { unique: false });
        tombstoneStore.createIndex("parentId", "parentId", { unique: false });
      }

      // 4. Pending Deletes Store
      if (!db.objectStoreNames.contains(STORE_PENDING_DELETES)) {
        const deleteStore = db.createObjectStore(STORE_PENDING_DELETES, {
          keyPath: "key",
        });
        deleteStore.createIndex("timestamp", "timestamp", { unique: false });
        deleteStore.createIndex("parentId", "parentId", { unique: false });
        deleteStore.createIndex("attempts", "attempts", { unique: false });
      }
    };

    request.onblocked = () => {
      reportFailure("openDB", {
        name: "Blocked",
        message: "Database blocked by another tab",
      });
    };
  });
};

/**
 * Save a pending upload to IndexedDB
 */
export const savePendingUpload = async (
  key,
  file,
  mutationInput,
  parentId,
  parentLabel,
) => {
  const data = {
    key,
    file,
    mutationInput,
    parentId,
    parentLabel,
    timestamp: Date.now(),
    fileSize: file.size,
    fileName: file.name,
    fileType: file.type,
  };

  const success = await withStore(
    STORE_PENDING,
    "readwrite",
    "savePendingUpload",
    (store) =>
      requestPromise(store.put(data), "savePendingUpload", {
        key,
        size: file.size,
      }).then(() => true),
    { fallback: false, metadata: { key }, rethrow: true },
  );

  return !!success;
};

/**
 * Get all pending uploads for a specific parent ID
 */
export const getPendingUploads = async (parentId) =>
  (await withStore(
    STORE_PENDING,
    "readonly",
    "getPendingUploads",
    (store) =>
      requestPromise(
        store.index("parentId").getAll(parentId),
        "getPendingUploads",
        { parentId },
      ),
    { fallback: [], metadata: { parentId } },
  )) ?? [];

/**
 * Get a single pending upload by key
 */
export const getPendingUploadByKey = async (key) =>
  withStore(
    STORE_PENDING,
    "readonly",
    "getPendingUploadByKey",
    (store) =>
      requestPromise(store.get(key), "getPendingUploadByKey", { key }).then(
        (result) => result || null,
      ),
    { fallback: null, metadata: { key } },
  );

/**
 * Delete a pending upload from IndexedDB
 */
export const deletePendingUpload = async (key) =>
  withStore(
    STORE_PENDING,
    "readwrite",
    "deletePendingUpload",
    (store) =>
      requestPromise(store.delete(key), "deletePendingUpload", { key }).then(
        () => true,
      ),
    { fallback: false, metadata: { key } },
  );

/**
 * Save a pending mutation to IndexedDB
 */
export const savePendingMutation = async (
  key,
  mutationInput,
  parentId,
  attempts = 0,
) => {
  const data = {
    key,
    mutationInput,
    parentId,
    attempts,
    timestamp: Date.now(),
    lastAttempt: attempts > 0 ? Date.now() : null,
  };

  const success = await withStore(
    STORE_MUTATIONS,
    "readwrite",
    "savePendingMutation",
    (store) =>
      requestPromise(store.put(data), "savePendingMutation", {
        key,
        attempts,
      }).then(() => true),
    { fallback: false, metadata: { key }, rethrow: true },
  );

  return !!success;
};

/**
 * Get all pending mutations for a specific parent ID
 */
export const getPendingMutations = async (parentId) =>
  (await withStore(
    STORE_MUTATIONS,
    "readonly",
    "getPendingMutations",
    (store) =>
      requestPromise(
        store.index("parentId").getAll(parentId),
        "getPendingMutations",
        { parentId },
      ),
    { fallback: [], metadata: { parentId } },
  )) ?? [];

/**
 * Get all pending mutations (across all parents)
 */
export const getAllPendingMutations = async () =>
  (await withStore(
    STORE_MUTATIONS,
    "readonly",
    "getAllPendingMutations",
    (store) =>
      requestPromise(store.getAll(), "getAllPendingMutations").then(
        (result) => result || [],
      ),
    { fallback: [] },
  )) ?? [];

/**
 * Delete a pending mutation from IndexedDB
 */
export const deletePendingMutation = async (key) =>
  withStore(
    STORE_MUTATIONS,
    "readwrite",
    "deletePendingMutation",
    (store) =>
      requestPromise(store.delete(key), "deletePendingMutation", { key }).then(
        () => true,
      ),
    { fallback: false, metadata: { key } },
  );

/**
 * Clear old uploads and mutations
 */
export const clearOldUploads = async (maxAgeMs = 7 * 24 * 60 * 60 * 1000) => {
  const pendingRemoved = await pruneByTimestamp(
    STORE_PENDING,
    "timestamp",
    maxAgeMs,
    "clearOldUploads_pending",
  );
  const mutationsRemoved = await pruneByTimestamp(
    STORE_MUTATIONS,
    "timestamp",
    maxAgeMs,
    "clearOldUploads_mutations",
  );
  return pendingRemoved + mutationsRemoved;
};

/**
 * Get storage usage statistics
 */
export const getStorageStats = async () => {
  const pendingUploads =
    (await withStore(
      STORE_PENDING,
      "readonly",
      "getStorageStats_pendingCount",
      (store) => requestPromise(store.count(), "getStorageStats_pendingCount"),
      { fallback: 0 },
    )) ?? 0;

  const pendingMutations =
    (await withStore(
      STORE_MUTATIONS,
      "readonly",
      "getStorageStats_mutationCount",
      (store) => requestPromise(store.count(), "getStorageStats_mutationCount"),
      { fallback: 0 },
    )) ?? 0;

  const totalSizeBytes =
    (await withStore(
      STORE_PENDING,
      "readonly",
      "getStorageStats_totalSize",
      (store) =>
        requestPromise(store.getAll(), "getStorageStats_totalSize").then(
          (uploads) =>
            (uploads || []).reduce(
              (sum, upload) => sum + (upload.fileSize || 0),
              0,
            ),
        ),
      { fallback: 0 },
    )) ?? 0;

  return {
    pendingUploads,
    pendingMutations,
    total: pendingUploads + pendingMutations,
    totalSizeBytes,
    totalSizeMB: (totalSizeBytes / 1024 / 1024).toFixed(2),
  };
};

/**
 * Clear all pending uploads for a specific parent
 */
export const clearPendingUploadsForParent = async (parentId) =>
  deleteByIndexMatch(
    STORE_PENDING,
    "parentId",
    parentId,
    "clearPendingUploadsForParent",
    { parentId },
  );

/**
 * Clear all pending mutations for a specific parent
 */
export const clearPendingMutationsForParent = async (parentId) =>
  deleteByIndexMatch(
    STORE_MUTATIONS,
    "parentId",
    parentId,
    "clearPendingMutationsForParent",
    { parentId },
  );

/**
 * Clear ALL data from IndexedDB
 */
export const clearAllData = async () => {
  try {
    const db = await openDB();

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_PENDING, STORE_MUTATIONS],
        "readwrite",
      );
      transaction.objectStore(STORE_PENDING).clear();
      transaction.objectStore(STORE_MUTATIONS).clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });

    return true;
  } catch (error) {
    reportFailure("clearAllData", error);
    return false;
  }
};

/**
 * Delete the entire IndexedDB database
 */
export const deleteDatabase = async () => {
  try {
    if (dbInstance) {
      dbInstance.close();
      dbInstance = null;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        reportFailure("deleteDatabase", request.error);
        reject(request.error);
      };
      request.onblocked = () => {
        reportFailure("deleteDatabase_blocked", {
          message: "Blocked by open tabs",
        });
      };
    });
  } catch (error) {
    reportFailure("deleteDatabase_catch", error);
    return false;
  }
};

export const isIndexedDBAvailable = () => {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
};

/**
 * Test IndexedDB functionality
 */
export const testIndexedDB = async () => {
  const results = {
    available: false,
    canOpen: false,
    canWrite: false,
    canRead: false,
    error: null,
  };

  try {
    results.available = isIndexedDBAvailable();
    if (!results.available) {
      results.error = "IndexedDB not available";
      return results;
    }

    await openDB();
    results.canOpen = true;

    const testFile = new File(["test"], "test.txt", { type: "text/plain" });
    const testKey = `test-parent/test_${Date.now()}.txt`;

    await savePendingUpload(testKey, testFile, {}, "test-parent");
    results.canWrite = true;

    const retrieved = await getPendingUploadByKey(testKey);
    results.canRead = retrieved !== null;

    await deletePendingUpload(testKey);
  } catch (error) {
    results.error = error.message;
    reportFailure("testIndexedDB", error);
  }

  return results;
};

// ==========================================
// TOMBSTONE OPERATIONS
// ==========================================

export const addTombstone = async (key, parentId) => {
  const data = { key, parentId, timestamp: Date.now() };
  return withStore(
    STORE_TOMBSTONES,
    "readwrite",
    "addTombstone",
    (store) =>
      requestPromise(store.put(data), "addTombstone", { key }).then(() => true),
    { fallback: false, metadata: { key } },
  );
};

export const getTombstones = async (parentId) => {
  const rows =
    (await withStore(
      STORE_TOMBSTONES,
      "readonly",
      "getTombstones",
      (store) =>
        requestPromise(
          store.index("parentId").getAll(parentId),
          "getTombstones",
          { parentId },
        ),
      { fallback: [], metadata: { parentId } },
    )) ?? [];
  return new Set(rows.map((t) => t.key));
};

export const removeTombstone = async (key) =>
  withStore(
    STORE_TOMBSTONES,
    "readwrite",
    "removeTombstone",
    (store) =>
      requestPromise(store.delete(key), "removeTombstone", { key }).then(
        () => true,
      ),
    { fallback: false, metadata: { key } },
  );

export const isTombstoned = async (key) =>
  !!(await withStore(
    STORE_TOMBSTONES,
    "readonly",
    "isTombstoned",
    (store) =>
      requestPromise(store.get(key), "isTombstoned", { key }).then(
        (result) => result !== undefined,
      ),
    { fallback: false, metadata: { key } },
  ));

export const clearOldTombstones = async (maxAgeMs = 30 * 24 * 60 * 60 * 1000) =>
  pruneByTimestamp(
    STORE_TOMBSTONES,
    "timestamp",
    maxAgeMs,
    "clearOldTombstones",
  );

export const savePendingDelete = async (
  key,
  photoId,
  parentId,
  attempts = 0,
) => {
  const data = {
    key,
    photoId,
    parentId,
    attempts,
    timestamp: Date.now(),
    lastAttempt: attempts > 0 ? Date.now() : null,
  };

  const success = await withStore(
    STORE_PENDING_DELETES,
    "readwrite",
    "savePendingDelete",
    (store) =>
      requestPromise(store.put(data), "savePendingDelete", { key }).then(
        () => true,
      ),
    { fallback: false, metadata: { key }, rethrow: true },
  );

  return !!success;
};

export const getPendingDeletes = async (parentId) => {
  return (
    (await withStore(
      STORE_PENDING_DELETES,
      "readonly",
      "getPendingDeletes",
      (store) =>
        requestPromise(
          store.index("parentId").getAll(parentId),
          "getPendingDeletes",
          { parentId },
        ),
      { fallback: [], metadata: { parentId } },
    )) ?? []
  );
};

export const deletePendingDelete = async (key) => {
  return withStore(
    STORE_PENDING_DELETES,
    "readwrite",
    "deletePendingDelete",
    (store) =>
      requestPromise(store.delete(key), "deletePendingDelete", { key }).then(
        () => true,
      ),
    { fallback: false, metadata: { key } },
  );
};

const uploadDbService = {
  generateSafeFileKey,
  savePendingUpload,
  getPendingUploads,
  getPendingUploadByKey,
  deletePendingUpload,
  savePendingMutation,
  getPendingMutations,
  getAllPendingMutations,
  deletePendingMutation,
  addTombstone,
  getTombstones,
  removeTombstone,
  isTombstoned,
  clearOldTombstones,
  savePendingDelete,
  getPendingDeletes,
  deletePendingDelete,
  clearOldUploads,
  getStorageStats,
  clearPendingUploadsForParent,
  clearPendingMutationsForParent,
  clearAllData,
  deleteDatabase,
  isIndexedDBAvailable,
  testIndexedDB,
};

export default uploadDbService;
